import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { candidateDomains, normalizeHexColor, pickMostSpecific } from '../lib/orgDomain';
import { useAuth } from './AuthContext';

export interface Organization {
  id: string;
  name: string;
  domain: string | null;
  hexcode: string | null;
  image_url: string | null;
}

interface OrganizationContextType {
  /** Organization resolved from the host, or the signed-in user's main organization. */
  organization: Organization | null;
  loading: boolean;
}

export const DEFAULT_TITLE = 'Portal REPSE';
export const DEFAULT_FAVICON = `${process.env.PUBLIC_URL || ''}/favicon.svg`;
const BRAND_STYLE_ID = 'org-brand-color';

// Safe default so components rendered outside the provider (e.g. unit tests) fall back to neutral branding.
const OrganizationContext = createContext<OrganizationContextType>({ organization: null, loading: false });

export const useOrganization = () => useContext(OrganizationContext);

export function applyBranding(organization: Organization | null) {
  document.title = organization?.name ? `${organization.name} · ${DEFAULT_TITLE}` : DEFAULT_TITLE;

  const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (favicon) {
    if (organization?.image_url) {
      // Logos can be PNG/JPG; a stale `type` would make browsers ignore the icon.
      favicon.removeAttribute('type');
      favicon.href = organization.image_url;
    } else {
      favicon.type = 'image/svg+xml';
      favicon.href = DEFAULT_FAVICON;
    }
  }

  // Scope the brand color to the light theme so dark mode keeps its accessible accent.
  const color = normalizeHexColor(organization?.hexcode);
  let style = document.getElementById(BRAND_STYLE_ID);
  if (!color) {
    style?.remove();
    return;
  }
  if (!style) {
    style = document.createElement('style');
    style.id = BRAND_STYLE_ID;
    document.head.appendChild(style);
  }
  // Light theme only; dark mode keeps its own accessible accent. The derived
  // strong/soft tones feed nav-active, tabs and report highlights in index.css.
  style.textContent = `html:not(.dark) { --action: ${color}; --action-strong: color-mix(in srgb, ${color} 76%, #000); --action-soft: color-mix(in srgb, ${color} 10%, #fff); }`;
}

/** Looks up the organization for a host, preferring the most specific domain match. */
export async function fetchOrganizationForHost(hostname: string): Promise<Organization | null> {
  const candidates = candidateDomains(hostname);
  if (!candidates.length) return null;
  const { data, error } = await supabase
    .from('organizations')
    .select('id, name, domain, hexcode, image_url')
    .in('domain', candidates);
  if (error) throw error;
  return pickMostSpecific(data as Organization[] | null, candidates);
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { userProfile } = useAuth();
  const [domainOrganization, setDomainOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const org = await fetchOrganizationForHost(window.location.hostname);
        if (!cancelled) setDomainOrganization(org);
      } catch (err) {
        console.warn('No organization found for this domain', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Hosts without a tenant (localhost, shared domains) fall back to the user's main organization.
  const userOrganization = useMemo<Organization | null>(() => {
    const memberships = userProfile?.user_organization || [];
    const membership =
      memberships.find((m) => m.is_active && m.is_main) || memberships.find((m) => m.is_active);
    const org = membership?.organization;
    if (!org) return null;
    return {
      id: org.id,
      name: org.name,
      hexcode: org.hexcode ?? null,
      image_url: org.image_url ?? null,
      domain: org.domain ?? null,
    };
  }, [userProfile]);

  const organization = domainOrganization || userOrganization;

  useEffect(() => {
    if (!loading) applyBranding(organization);
  }, [organization, loading]);

  const value = useMemo(() => ({ organization, loading }), [organization, loading]);
  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}
